import { Link, useLocation, Outlet } from 'react-router-dom'
import { clsx } from 'clsx'
import { useAuth } from '@/contexts/AuthContext'
import { useTheme } from '@/contexts/ThemeContext'
import { useEasterEgg } from '@/contexts/EasterEggContext'
import { useState, useRef, useEffect } from 'react'
import { Moon, Sun, Menu, X, Home, ChevronDown, PanelRight, Library } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { api, setEnabledLanguages } from '@/services/api'
import SidePanel from '@/components/panel/SidePanel'
import { useSidePanelStore } from '@/stores/sidePanelStore'
import CardDetailModal from '@/components/collection/CardDetailModal'

function SidePanelToggle() {
  const { isOpen, toggle } = useSidePanelStore()
  return (
    <button
      onClick={toggle}
      className={`p-1.5 rounded transition-colors ${
        isOpen
          ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
          : 'text-gray-500 dark:text-dark-muted hover:text-gray-700 dark:hover:text-dark-text'
      }`}
      title="Toggle side panel"
    >
      <PanelRight className="w-5 h-5" />
    </button>
  )
}

export default function Layout() {
  const location = useLocation()
  const { user, logout, isImpersonating, stopImpersonating } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const { activateEasterEgg, isEasterEggActive } = useEasterEgg()
  const [showDropdown, setShowDropdown] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [typedSequence, setTypedSequence] = useState('')
  const [tapCount, setTapCount] = useState(0)
  const tapTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  
  // Fetch user preferences to check if Player Set mode is enabled
  const { data: userPreferences } = useQuery({
    queryKey: ['user-preferences'],
    queryFn: async () => {
      if (!user) return null
      const response = await api.get('/api/auth/preferences')
      return response.data
    },
    enabled: !!user
  })
  
  const isPlayerSetMode = userPreferences?.collection_mode === 'player_set'
  const isVendorMode = userPreferences?.vendor_mode === true
  const isSlabsEnabled = userPreferences?.slabs_enabled === true

  // Sync enabled languages to localStorage for API interceptor
  useEffect(() => {
    if (userPreferences?.enabled_languages) {
      setEnabledLanguages(userPreferences.enabled_languages)
    }
  }, [userPreferences?.enabled_languages])

  const navGroups = [
    {
      label: 'Collection',
      items: [
        { path: '/binders', label: 'Binders' },
        { path: '/bulkboxes', label: 'Bulkboxes' },
        { path: '/collection/sealed', label: 'Sealed' },
        ...(isPlayerSetMode ? [
          { path: '/collection/player-set', label: 'Player Set' },
          { path: '/deck-builder', label: 'Deck Builder' },
        ] : []),
        { path: '/wants', label: 'Wants' },
        ...(isSlabsEnabled ? [{ path: '/slabs', label: 'Slabs' }] : []),
      ]
    },
    ...(isVendorMode ? [{
      label: 'Vendor',
      items: [
        { path: '/dashboard', label: 'Dashboard' },
        { path: '/inventory/cards', label: 'Card Manager' },
        { path: '/inventory/intake', label: 'Intake' },
        { path: '/inventory/ledger', label: 'Ledger' },
        { path: '/inventory/comp', label: 'Comp' },
        { path: '/orders', label: 'Orders' },
        { path: '/breaks', label: 'Breaks' },
        { path: '/metrics', label: 'Metrics' },
        { path: '/price-scans', label: 'Price Scans' },
        { path: '/store/manage', label: 'Store' },
      ]
    }, {
      label: 'Trading',
      items: [
        { path: '/trading', label: 'Market Overview' },
        { path: '/trading/portfolio', label: 'Portfolio' },
        { path: '/trading/scanner', label: 'Scanner' },
        { path: '/trading/heatmap', label: 'Heatmap' },
        { path: '/trading/cardtrader', label: 'CardTrader' },
        { path: '/trading/builder', label: 'Dashboard Builder' },
      ]
    }] : []),
  ]

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Easter egg: detect "boobies" typing
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Ignore if user is typing in an input or textarea
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return
      }

      const newSequence = typedSequence + event.key.toLowerCase()
      setTypedSequence(newSequence)

      // Check if "boobies" was typed
      if (newSequence.includes('boobies')) {
        activateEasterEgg('keyboard')
        setTypedSequence('')
      }

      // Clear sequence if it gets too long
      if (newSequence.length > 20) {
        setTypedSequence('')
      }
    }

    document.addEventListener('keypress', handleKeyPress)
    return () => document.removeEventListener('keypress', handleKeyPress)
  }, [typedSequence])

  // Handle logo tap for mobile easter egg activation
  const handleLogoTap = () => {
    // Clear existing timeout
    if (tapTimeoutRef.current) {
      clearTimeout(tapTimeoutRef.current)
    }

    // Increment tap count
    const newTapCount = tapCount + 1
    setTapCount(newTapCount)

    // Check if we've reached 8 taps
    if (newTapCount >= 8) {
      activateEasterEgg('tap')
      setTapCount(0)
      
      // Add haptic feedback if available
      if ('vibrate' in navigator) {
        navigator.vibrate(200)
      }
    } else {
      // Reset tap count after 2 seconds of no taps
      tapTimeoutRef.current = setTimeout(() => {
        setTapCount(0)
      }, 2000)
    }
  }

  return (
    <>
      <nav className="bg-white dark:bg-dark-surface shadow-lg sticky top-0 z-50 border-b border-gray-200 dark:border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between" style={{ minHeight: '4rem' }}>
            <div className="flex">
              {/* Mobile menu button */}
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="lg:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-bg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              >
                {showMobileMenu ? (
                  <X className="block h-6 w-6" />
                ) : (
                  <Menu className="block h-6 w-6" />
                )}
              </button>
              
              <div className="flex-shrink-0 flex items-center ml-2 lg:ml-0">
                <div className="flex flex-col items-start">
                  <h1 
                    onClick={handleLogoTap}
                    className={clsx(
                      "text-lg sm:text-xl font-bold transition-all duration-500 transform cursor-pointer select-none",
                      isEasterEggActive 
                        ? "text-pink-500 animate-pulse scale-110" 
                        : "bg-gradient-to-r from-red-600 to-blue-600 bg-clip-text text-transparent"
                    )}
                    style={isEasterEggActive ? { 
                      color: '#FF1493', 
                      textShadow: '0 0 20px #FF1493, 0 0 40px #FF69B4',
                      animation: 'pulse 1s infinite'
                    } : {}}
                  >
                    One Piece TCG Collector
                  </h1>
                  {isEasterEggActive && (
                    <span 
                      className="text-xs animate-pulse"
                      style={{ 
                        color: '#FF1493',
                        textShadow: '0 0 5px #FF1493',
                        marginTop: '-4px',
                        letterSpacing: '0.05em'
                      }}
                    >
                      Boobies-mode Activated
                    </span>
                  )}
                </div>
              </div>
              <div className="hidden lg:ml-6 lg:flex lg:items-center lg:gap-1">
                <Link
                  to="/"
                  className={clsx(
                    'p-2 rounded-lg transition-colors',
                    location.pathname === '/'
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                      : 'text-gray-500 dark:text-dark-muted hover:bg-gray-100 dark:hover:bg-dark-border hover:text-gray-900 dark:hover:text-dark-text'
                  )}
                  title="Home"
                >
                  <Home className="h-4 w-4" />
                </Link>
                <Link
                  to="/collection"
                  className={clsx(
                    'flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                    location.pathname === '/collection'
                      ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                      : 'text-gray-600 dark:text-dark-muted hover:bg-gray-100 dark:hover:bg-dark-border hover:text-gray-900 dark:hover:text-dark-text'
                  )}
                >
                  <Library className="h-4 w-4" />
                  My Collection
                </Link>
                <Link
                  to="/collection/all-cards"
                  className={clsx(
                    'px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                    location.pathname === '/collection/all-cards'
                      ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                      : 'text-gray-600 dark:text-dark-muted hover:bg-gray-100 dark:hover:bg-dark-border hover:text-gray-900 dark:hover:text-dark-text'
                  )}
                >
                  All Cards
                </Link>
                {navGroups.map((group) => {
                  const groupActive = group.items.some(item => location.pathname === item.path)
                  return (
                    <div key={group.label} className="relative group/nav">
                      <button
                        className={clsx(
                          'flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                          groupActive
                            ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                            : 'text-gray-600 dark:text-dark-muted hover:bg-gray-100 dark:hover:bg-dark-border hover:text-gray-900 dark:hover:text-dark-text'
                        )}
                      >
                        {group.label}
                        <ChevronDown className="h-3.5 w-3.5 opacity-50 group-hover/nav:opacity-100 transition-opacity" />
                      </button>
                      <div className="absolute left-0 top-full pt-1 invisible group-hover/nav:visible opacity-0 group-hover/nav:opacity-100 transition-all duration-150 z-50">
                        <div className="bg-white dark:bg-dark-surface rounded-lg shadow-lg border border-gray-200 dark:border-dark-border py-1 min-w-[180px]">
                          {group.items.map((item) => (
                            <Link
                              key={item.path}
                              to={item.path}
                              className={clsx(
                                'block px-4 py-2 text-sm transition-colors',
                                location.pathname === item.path
                                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium'
                                  : 'text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-dark-bg'
                              )}
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
            <div className="hidden lg:flex items-center space-x-4">
              {/* Side Panel Toggle */}
              {user && <SidePanelToggle />}
              {user ? (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="flex items-center space-x-2 text-sm text-gray-700 hover:text-gray-900 focus:outline-none"
                  >
                    {user.avatar_url ? (
                      <img
                        src={user.avatar_url}
                        alt={user.display_name || user.email}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-red-500 to-blue-500 flex items-center justify-center text-white font-bold">
                        {(user.display_name || user.email).charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span>{user.display_name || user.email}</span>
                    <svg
                      className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {showDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-dark-surface rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-dark-border">
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-bg"
                        onClick={() => setShowDropdown(false)}
                      >
                        Profile Settings
                      </Link>
                      <Link
                        to="/settings"
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-bg"
                        onClick={() => setShowDropdown(false)}
                      >
                        App Settings
                      </Link>
                      <Link
                        to="/tampermonkey"
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-bg"
                        onClick={() => setShowDropdown(false)}
                      >
                        Tampermonkey Script
                      </Link>
                      <button
                        onClick={toggleTheme}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-bg flex items-center justify-between"
                      >
                        <span>Theme</span>
                        <div className="flex items-center gap-2">
                          {theme === 'light' ? (
                            <>
                              <Sun className="h-4 w-4" />
                              <span className="text-xs">Light</span>
                            </>
                          ) : (
                            <>
                              <Moon className="h-4 w-4" />
                              <span className="text-xs">Dark</span>
                            </>
                          )}
                        </div>
                      </button>
                      {user.is_admin && (
                        <>
                          <hr className="my-1" />
                          <Link
                            to="/admin"
                            className="block px-4 py-2 text-sm text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 font-medium"
                            onClick={() => setShowDropdown(false)}
                          >
                            Admin Panel
                          </Link>
                        </>
                      )}
                      <hr className="my-1" />
                      <button
                        onClick={() => {
                          logout()
                          setShowDropdown(false)
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-bg"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="text-sm font-medium text-blue-600 hover:text-blue-500"
                >
                  Login
                </Link>
              )}
            </div>
          </div>
        </div>
        
        {/* Mobile menu panel */}
        {showMobileMenu && (
          <div className="lg:hidden bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border">
            <div className="pt-2 pb-3 space-y-1">
              <Link
                to="/"
                onClick={() => setShowMobileMenu(false)}
                className={clsx(
                  'block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors',
                  location.pathname === '/'
                    ? 'bg-red-50 dark:bg-red-900/20 border-red-500 text-red-700 dark:text-red-400'
                    : 'border-transparent text-gray-600 dark:text-dark-muted hover:bg-gray-50 dark:hover:bg-dark-bg hover:border-gray-300 dark:hover:border-dark-border hover:text-gray-800 dark:hover:text-dark-text'
                )}
              >
                Home
              </Link>
              <Link
                to="/collection"
                onClick={() => setShowMobileMenu(false)}
                className={clsx(
                  'flex items-center gap-2 pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors',
                  location.pathname === '/collection'
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 text-blue-700 dark:text-blue-400'
                    : 'border-transparent text-gray-600 dark:text-dark-muted hover:bg-gray-50 dark:hover:bg-dark-bg hover:border-gray-300 dark:hover:border-dark-border hover:text-gray-800 dark:hover:text-dark-text'
                )}
              >
                <Library className="h-4 w-4" />
                My Collection
              </Link>
              <Link
                to="/collection/all-cards"
                onClick={() => setShowMobileMenu(false)}
                className={clsx(
                  'block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors',
                  location.pathname === '/collection/all-cards'
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 text-blue-700 dark:text-blue-400'
                    : 'border-transparent text-gray-600 dark:text-dark-muted hover:bg-gray-50 dark:hover:bg-dark-bg hover:border-gray-300 dark:hover:border-dark-border hover:text-gray-800 dark:hover:text-dark-text'
                )}
              >
                All Cards
              </Link>

              {navGroups.map((group) => (
                <div key={group.label}>
                  <div className="px-3 py-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                    {group.label}
                  </div>
                  {group.items.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setShowMobileMenu(false)}
                      className={clsx(
                        'block pl-6 pr-4 py-2 border-l-4 text-sm font-medium transition-colors',
                        location.pathname === item.path
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 text-blue-700 dark:text-blue-400'
                          : 'border-transparent text-gray-600 dark:text-dark-muted hover:bg-gray-50 dark:hover:bg-dark-bg hover:border-gray-300 dark:hover:border-dark-border hover:text-gray-800 dark:hover:text-dark-text'
                      )}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              ))}
            </div>
            
            {/* Mobile user section */}
            <div className="border-t border-gray-200 dark:border-dark-border pt-3 pb-3">
              {user ? (
                <>
                  <div className="flex items-center px-4 mb-3">
                    {user.avatar_url ? (
                      <img
                        src={user.avatar_url}
                        alt={user.display_name || user.email}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-red-500 to-blue-500 flex items-center justify-center text-white font-bold">
                        {(user.display_name || user.email).charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="ml-3">
                      <div className="text-base font-medium text-gray-800 dark:text-dark-text">
                        {user.display_name || user.email}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Link
                      to="/profile"
                      onClick={() => setShowMobileMenu(false)}
                      className="block pl-6 pr-4 py-2 border-l-4 border-transparent text-sm font-medium text-gray-600 dark:text-dark-muted hover:bg-gray-50 dark:hover:bg-dark-bg hover:border-gray-300 dark:hover:border-dark-border hover:text-gray-800 dark:hover:text-dark-text"
                    >
                      Profile Settings
                    </Link>
                    <Link
                      to="/settings"
                      onClick={() => setShowMobileMenu(false)}
                      className="block pl-6 pr-4 py-2 border-l-4 border-transparent text-sm font-medium text-gray-600 dark:text-dark-muted hover:bg-gray-50 dark:hover:bg-dark-bg hover:border-gray-300 dark:hover:border-dark-border hover:text-gray-800 dark:hover:text-dark-text"
                    >
                      App Settings
                    </Link>
                    <Link
                      to="/tampermonkey"
                      onClick={() => setShowMobileMenu(false)}
                      className="block pl-6 pr-4 py-2 border-l-4 border-transparent text-sm font-medium text-gray-600 dark:text-dark-muted hover:bg-gray-50 dark:hover:bg-dark-bg hover:border-gray-300 dark:hover:border-dark-border hover:text-gray-800 dark:hover:text-dark-text"
                    >
                      Tampermonkey Script
                    </Link>
                    <button
                      onClick={() => {
                        toggleTheme()
                        setShowMobileMenu(false)
                      }}
                      className="w-full text-left pl-6 pr-4 py-2 border-l-4 border-transparent text-sm font-medium text-gray-600 dark:text-dark-muted hover:bg-gray-50 dark:hover:bg-dark-bg hover:border-gray-300 dark:hover:border-dark-border hover:text-gray-800 dark:hover:text-dark-text flex items-center justify-between"
                    >
                      <span>Theme</span>
                      <div className="flex items-center gap-2">
                        {theme === 'light' ? (
                          <>
                            <Sun className="h-4 w-4" />
                            <span className="text-xs">Light</span>
                          </>
                        ) : (
                          <>
                            <Moon className="h-4 w-4" />
                            <span className="text-xs">Dark</span>
                          </>
                        )}
                      </div>
                    </button>
                    {user.is_admin && (
                      <Link
                        to="/admin"
                        onClick={() => setShowMobileMenu(false)}
                        className="block pl-6 pr-4 py-2 border-l-4 border-transparent text-sm font-medium text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:border-purple-300 dark:hover:border-purple-700"
                      >
                        Admin Panel
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        logout()
                        setShowMobileMenu(false)
                      }}
                      className="block w-full text-left pl-6 pr-4 py-2 border-l-4 border-transparent text-sm font-medium text-gray-600 dark:text-dark-muted hover:bg-gray-50 dark:hover:bg-dark-bg hover:border-gray-300 dark:hover:border-dark-border hover:text-gray-800 dark:hover:text-dark-text"
                    >
                      Logout
                    </button>
                  </div>
                </>
              ) : (
                <div className="px-4">
                  <Link
                    to="/login"
                    onClick={() => setShowMobileMenu(false)}
                    className="block w-full text-center px-4 py-2 text-base font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Login
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>
      {isImpersonating && (
        <div className="bg-orange-500 text-white px-4 py-2 flex items-center justify-center gap-3 text-sm font-medium sticky top-16 z-40">
          <span>Impersonating: {user?.display_name || user?.email}</span>
          <button
            onClick={stopImpersonating}
            className="px-3 py-1 bg-white text-orange-600 rounded font-semibold hover:bg-orange-100 transition-colors"
          >
            Stop Impersonating
          </button>
        </div>
      )}
      <PinnableMain isVendorMode={isVendorMode} user={user} />
      <GlobalCardModal />
    </>
  )
}

function PinnableMain({ isVendorMode, user }: { isVendorMode: boolean; user: any }) {
  const isOpen = useSidePanelStore((s) => s.isOpen)
  const panelVisible = user && isOpen

  return (
    <div className="flex flex-1">
      <main className={panelVisible ? 'flex-1 min-w-0' : 'w-full'}>
        <Outlet />
      </main>
      {user && <SidePanel isVendorMode={isVendorMode} />}
    </div>
  )
}

function GlobalCardModal() {
  const { globalModalCard, closeCardModal } = useSidePanelStore()
  return (
    <CardDetailModal
      card={globalModalCard}
      isOpen={!!globalModalCard}
      onClose={closeCardModal}
      quantity={0}
    />
  )
}